import { getReturnIdsFromDatasetInstruction } from 'helpers/dataset.helper/dataset.helper'
import { TEMPLATE_SENTINEL_ID } from 'ui/uiConfig/template/defaultUiConfig'
import { ChildNodeInfo, ConfigNodeInfo, DsiTemplateInfo, RelationsTreeData } from './UiConfigRelationsTree.types'

type BuildNodeInfoArgs = {
  id: number
  idToUiConfig: UiConfigCollectionById
  singletonNames: Set<string>
  getTitle: (componentType: string) => string | null
}

export function buildNodeInfo({ id, idToUiConfig, singletonNames, getTitle }: BuildNodeInfoArgs): ConfigNodeInfo {
  const config = idToUiConfig[id] ?? null
  const uid = config?.uid ?? id
  const componentType = config?.component ?? ''
  const versionLabel = `${id}.${config?.version ?? 0}`
  const nameLabel = config?.display_name || getTitle(componentType) || componentType

  return {
    id,
    uid,
    config,
    displayName: `${versionLabel} ${nameLabel}`.trim(),
    componentType,
    isAnchor: singletonNames.has(componentType),
    isTemplate: id < 0,
  }
}

type ResolveTemplateDsisArgs = {
  datasetInstructions: Partial<DatasetInstruction>[]
  baseConfigDsiReturnIds: Set<string>
  codeTemplateDsiReturnIds: Set<string>
}

export function resolveTemplateDsis({
  datasetInstructions,
  baseConfigDsiReturnIds,
  codeTemplateDsiReturnIds,
}: ResolveTemplateDsisArgs): DsiTemplateInfo[] {
  return datasetInstructions
    .filter((dsi) => dsi.type === 'template')
    .map((dsi) => {
      const returnId = dsi.return_id ?? '(unknown)'
      let resolvedFrom: DsiTemplateInfo['resolvedFrom'] = 'unknown'

      if (returnId !== '(unknown)') {
        if (baseConfigDsiReturnIds.has(returnId)) {
          resolvedFrom = 'base_config'
        } else if (codeTemplateDsiReturnIds.has(returnId)) {
          resolvedFrom = 'code_template'
        }
      }

      return { return_id: returnId, resolvedFrom }
    })
}

export function getAncestors(
  currentId: number,
  uiConfigs: UiConfigCollection,
  idToUiConfig: UiConfigCollectionById,
  singletonNames: Set<string>,
  getTitle: (componentType: string) => string | null
): ConfigNodeInfo[] {
  const ancestors: ConfigNodeInfo[] = []
  const seenConfigIds = new Set<number>()

  Object.values(uiConfigs).forEach((config) => {
    const configId = config.id
    if (typeof configId !== 'number') {
      return
    }
    if (
      configId > 0 &&
      configId !== currentId &&
      !seenConfigIds.has(configId) &&
      config.children_ids?.includes(currentId)
    ) {
      seenConfigIds.add(configId)
      ancestors.push(
        buildNodeInfo({
          id: configId,
          idToUiConfig,
          singletonNames,
          getTitle,
        })
      )
    }
  })

  return ancestors
}

type GetChildrenArgs = {
  unparsedConfig: UiConfig
  parsedChildrenIds: number[]
  idToUiConfig: UiConfigCollectionById
  singletonNames: Set<string>
  getTitle: (componentType: string) => string | null
}

export function getChildren({
  unparsedConfig,
  parsedChildrenIds,
  idToUiConfig,
  singletonNames,
  getTitle,
}: GetChildrenArgs): ChildNodeInfo[] {
  const unparsedChildrenIds = new Set<number>(unparsedConfig.children_ids ?? [])

  return parsedChildrenIds
    .filter((id) => id !== TEMPLATE_SENTINEL_ID)
    .map((id) => ({
      ...buildNodeInfo({ id, idToUiConfig, singletonNames, getTitle }),
      isTemplate: id < 0,
      isDefaultTemplateChild: !unparsedChildrenIds.has(id),
    }))
}

export function getBaseConfigUsage(config: UiConfig): {
  hasBaseItems: boolean
  usesDefault: boolean
  usesBaseConfig: boolean
} {
  const hasBaseItems = config.props?.base_items != null && Object.keys(config.props.base_items).length > 0
  const usesDefault = !!config.props?.use_default

  return {
    hasBaseItems,
    usesDefault,
    usesBaseConfig: hasBaseItems || usesDefault,
  }
}

type BuildRelationsTreeDataArgs = {
  editingUid: UiConfigUid
  uiConfigs: UiConfigCollection
  idToUiConfig: UiConfigCollectionById
  baseUiConfig: UiConfig | null
  parsedChildrenIds: number[]
  unparsedConfig: UiConfig | null
  singletonNames: Set<string>
  getTitle: (componentType: string) => string | null
  codeTemplateDsiReturnIds: Set<string>
}

export function buildRelationsTreeData({
  editingUid,
  uiConfigs,
  idToUiConfig,
  baseUiConfig,
  parsedChildrenIds,
  unparsedConfig,
  singletonNames,
  getTitle,
  codeTemplateDsiReturnIds,
}: BuildRelationsTreeDataArgs): RelationsTreeData | null {
  const config = uiConfigs[editingUid]
  if (config?.id == null) {
    return null
  }

  const currentId = config.id as number
  const ancestors = getAncestors(currentId, uiConfigs, idToUiConfig, singletonNames, getTitle)

  const children = getChildren({
    unparsedConfig: unparsedConfig ?? config,
    parsedChildrenIds,
    idToUiConfig,
    singletonNames,
    getTitle,
  })

  const { hasBaseItems, usesDefault, usesBaseConfig } = getBaseConfigUsage(config)

  const baseConfigNode = baseUiConfig
    ? buildNodeInfo({
        id: baseUiConfig.id as number,
        idToUiConfig,
        singletonNames,
        getTitle,
      })
    : null

  const baseConfigDsiReturnIds = new Set<string>(
    [...(baseUiConfig?.dataset_instructions ?? []), ...(baseUiConfig?.props?.dataset_instructions ?? [])].flatMap((d) =>
      getReturnIdsFromDatasetInstruction(d)
    )
  )

  const templateDsis = resolveTemplateDsis({
    datasetInstructions: config.dataset_instructions ?? [],
    baseConfigDsiReturnIds,
    codeTemplateDsiReturnIds,
  })

  const selectedNode = buildNodeInfo({
    id: currentId,
    idToUiConfig,
    singletonNames,
    getTitle,
  })

  return {
    ancestors,
    selectedNode,
    children,
    usesBaseConfig,
    hasBaseItems,
    usesDefault,
    templateDsis,
    baseConfigNode,
  }
}
